import { useState, useRef, useEffect, useCallback } from 'react';

// ─── Predefined tag suggestions ───────────────────────────────────────────────
export const PREDEFINED_TAGS = [
  'Best Performers', 'Spring Sale', 'Summer Sale', 'Fall Sale', 'Winter Sale', 'Holiday',
  'Facebook Ads', 'Instagram Ads', 'Google Ads', 'TikTok Ads', 'LinkedIn Ads',
  'A/B Test', 'High CTR', 'Retargeting', 'Brand Awareness', 'Lead Gen',
  'Product Launch', 'Promo', 'Client Work', 'Archive',
];

// ─── Tag color palette ────────────────────────────────────────────────────────
const TAG_PALETTE = [
  { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-400' },
  { bg: 'bg-amber-100',  text: 'text-amber-700',  border: 'border-amber-200',  dot: 'bg-amber-400'  },
  { bg: 'bg-emerald-100',text: 'text-emerald-700',border: 'border-emerald-200',dot: 'bg-emerald-500'},
  { bg: 'bg-teal-100',   text: 'text-teal-700',   border: 'border-teal-200',   dot: 'bg-teal-500'   },
  { bg: 'bg-rose-100',   text: 'text-rose-700',   border: 'border-rose-200',   dot: 'bg-rose-400'   },
  { bg: 'bg-sky-100',    text: 'text-sky-700',    border: 'border-sky-200',    dot: 'bg-sky-400'    },
  { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-200', dot: 'bg-violet-400' },
  { bg: 'bg-pink-100',   text: 'text-pink-700',   border: 'border-pink-200',   dot: 'bg-pink-400'   },
  { bg: 'bg-lime-100',   text: 'text-lime-700',   border: 'border-lime-200',   dot: 'bg-lime-500'   },
  { bg: 'bg-cyan-100',   text: 'text-cyan-700',   border: 'border-cyan-200',   dot: 'bg-cyan-500'   },
];

export function getTagColor(tag: string) {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = tag.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }
  return TAG_PALETTE[Math.abs(hash) % TAG_PALETTE.length];
}

// ─── TagBadge (display-only pill) ────────────────────────────────────────────
export function TagBadge({ tag, onRemove }: { tag: string; onRemove?: () => void }) {
  const c = getTagColor(tag);
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${c.bg} ${c.text} ${c.border} whitespace-nowrap`}>
      {tag}
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="w-3 h-3 flex items-center justify-center rounded-full opacity-50 hover:opacity-100 cursor-pointer transition-opacity"
        >
          <i className="ri-close-line" style={{ fontSize: '9px' }} />
        </button>
      )}
    </span>
  );
}

// ─── TagManager (full editor) ─────────────────────────────────────────────────
interface TagManagerProps {
  itemId: string;
  tags: string[];
  /** All unique tags used across all history items — shown as suggestions */
  allExistingTags: string[];
  onUpdate: (id: string, newTags: string[]) => Promise<void>;
}

export function TagManager({ itemId, tags, allExistingTags, onUpdate }: TagManagerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Auto-focus input
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 40);
  }, [isOpen]);

  const allSuggestions = Array.from(new Set([...PREDEFINED_TAGS, ...allExistingTags]));
  const filtered = allSuggestions
    .filter(t => !tags.includes(t))
    .filter(t => search === '' || t.toLowerCase().includes(search.toLowerCase()));

  const trimmed = search.trim();
  const canCreate =
    trimmed.length > 0 &&
    !tags.some(t => t.toLowerCase() === trimmed.toLowerCase()) &&
    !allSuggestions.some(t => t.toLowerCase() === trimmed.toLowerCase());

  const handleAdd = useCallback(async (tag: string) => {
    const t = tag.trim();
    if (!t || tags.includes(t)) return;
    setSaving(true);
    setSearch('');
    await onUpdate(itemId, [...tags, t]);
    setSaving(false);
  }, [itemId, tags, onUpdate]);

  const handleRemove = useCallback(async (tag: string) => {
    setSaving(true);
    await onUpdate(itemId, tags.filter(t => t !== tag));
    setSaving(false);
  }, [itemId, tags, onUpdate]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[0]) handleAdd(filtered[0]);
      else if (canCreate) handleAdd(trimmed);
    }
    if (e.key === 'Escape') { setIsOpen(false); setSearch(''); }
  };

  return (
    <div className="relative" ref={containerRef} onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-wrap items-center gap-1.5">
        {tags.map(tag => (
          <TagBadge
            key={tag}
            tag={tag}
            onRemove={saving ? undefined : () => handleRemove(tag)}
          />
        ))}

        {/* Add tag button */}
        <button
          onClick={() => setIsOpen(o => !o)}
          disabled={saving}
          className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border border-dashed transition-all duration-150 cursor-pointer whitespace-nowrap ${
            saving
              ? 'border-stone-200 text-stone-300 pointer-events-none'
              : 'border-stone-300 text-stone-400 hover:border-orange-400 hover:text-orange-500 hover:bg-orange-50'
          }`}
        >
          {saving
            ? <i className="ri-loader-4-line animate-spin text-xs" />
            : <i className="ri-add-line text-xs" />
          }
          {tags.length === 0 && <span>Add tag</span>}
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute left-0 top-full mt-2 z-50 bg-white border border-stone-200 rounded-xl overflow-hidden"
          style={{ width: '224px', boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}
        >
          {/* Search input */}
          <div className="px-3 pt-2.5 pb-2 border-b border-stone-100">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search or create tag…"
              className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all"
            />
          </div>

          <div className="max-h-52 overflow-y-auto py-1.5">
            {/* Create new tag option */}
            {canCreate && (
              <button
                onClick={() => handleAdd(trimmed)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left hover:bg-orange-50 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center rounded-md bg-orange-100 shrink-0">
                  <i className="ri-add-line text-orange-500 text-xs" />
                </div>
                <span className="text-stone-700">Create <strong>&quot;{trimmed}&quot;</strong></span>
              </button>
            )}

            {filtered.length === 0 && !canCreate && (
              <p className="px-3 py-3 text-xs text-stone-400 text-center">
                {search ? 'No matching tags' : 'All tags added'}
              </p>
            )}

            {filtered.map(tag => {
              const c = getTagColor(tag);
              return (
                <button
                  key={tag}
                  onClick={() => handleAdd(tag)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left hover:bg-stone-50 transition-colors cursor-pointer group"
                >
                  <div className={`w-2 h-2 rounded-full shrink-0 ${c.dot}`} />
                  <span className="text-stone-700 flex-1">{tag}</span>
                  <i className="ri-add-line text-stone-300 group-hover:text-orange-500 text-xs transition-colors" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
